import {Component, EventEmitter, Input, OnInit, Output, inject} from '@angular/core';
import {MfaAuthenticationResponse} from '../../_data/mfa/mfa-authentication';
import {UntypedFormBuilder, UntypedFormGroup, Validators, ReactiveFormsModule} from '@angular/forms';
import {PingErrorId} from '../../_constants/types';
import {MfaService} from '../../_services/data/mfa.service';
import {JwtToken} from '../../_data/admin/jwt-token';
import {DeviceDetails, UserDetails} from '../../_data/mfa/mfa-user-details';
import {MfaFinishPairingOrAuthResponse, MfaUserDeviceId, MfaUserSessionId} from '../../_data/mfa/mfa-pairing';
import {mergeMap, tap} from 'rxjs/operators';
import {NotificationService} from '../../_services/gui/notification.service';
import {Observable, of, Subject} from 'rxjs';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {NgTemplateOutlet, NgClass} from '@angular/common';
import {ValidationErrorsComponent} from '../../common/forms/validation-errors/validation-errors.component';
import {TranslateModule} from '@ngx-translate/core';

@Component({
    selector: 'app-mfa-authenticate',
    templateUrl: './authenticate.component.html',
    styleUrls: ['./authenticate.component.scss'],
    imports: [FaIconComponent, NgTemplateOutlet, ReactiveFormsModule, NgClass, ValidationErrorsComponent, TranslateModule]
})
export class AuthenticateComponent implements OnInit {

  @Input() userName: string;
  @Input() jwtToken: JwtToken<UserDetails>;
  @Input() initialDevice: DeviceDetails;

  @Output() newJwtToken: EventEmitter<JwtToken<any>> = new EventEmitter<JwtToken<any>>();
  @Output() cancel: EventEmitter<void> = new EventEmitter<void>();

  lastUsedDevice: DeviceDetails;
  mfaAuthenticationResponse: MfaAuthenticationResponse;

  formGroup: UntypedFormGroup;
  formSubmitted: boolean = false;

  mfaAuthenticationDisplayId: string;

  initialRequestSent: boolean = false;

  readonly formBuilder = inject(UntypedFormBuilder);
  readonly mfaService = inject(MfaService);
  private readonly notificationService = inject(NotificationService);

  private _showAppDetails: boolean = false;
  private _showAppFailure: boolean = false;
  private _showOtpForm: boolean = false;
  private _showOtherWays: boolean = false;
  private _showIncorrectCode: boolean = false;

  private cancelOtherWaysCallback: any;

  constructor() {
    const formBuilder = this.formBuilder;

    this.formGroup = formBuilder.group({
      code: ['', Validators.required]
    });
  }

  private initDisplayStates() {
    this.initialRequestSent = false;
    this._showAppDetails = false;
    this._showAppFailure = false;
    this._showOtpForm = false;
    this._showOtherWays = false;
    this._showIncorrectCode = false;
    this.formSubmitted = false;
  }

  private resetForm() {
    this.formGroup.controls.code.setValue('');
    this.formSubmitted = false;
    this.formSubmitted = false;
  }

  private getMfaAuthenticationDisplayId(mfaAuthenticationResponse: MfaAuthenticationResponse): string {
    //Sometimes the response doesn't include the userDevices, so we can get that from the stored jwtToken if we need to
    const userDevices: DeviceDetails[] = mfaAuthenticationResponse?.userDevices ?? this.jwtToken?.mfaUserDetails?.devicesDetails;
    console.debug('authenticatingDeviceId', mfaAuthenticationResponse.authenticatingDeviceId);
    console.debug('userDevices', userDevices);
    //Find the device by it's Uuid
    return userDevices?.find(d => d.deviceUuid === mfaAuthenticationResponse.authenticatingDeviceId)?.displayID ?? mfaAuthenticationResponse?.authEvaluationDataResponse?.authType;
  }

  ngOnInit(): void {
    this.initDisplayStates();
    // this.chooseOtherDevice();
    this.authenticateWith(this.initialDevice);
  }

  submitMfaCode(value: any) {
    this.formSubmitted = true;
    if(this.formGroup.valid) {
      this.mfaService.finishAuthentication(
        {userMFADetails: {userName: this.userName}, sessionId: this.mfaAuthenticationResponse.sessionId, otpCode: value.code},
        this.jwtToken
      ).subscribe((jwtToken: JwtToken<MfaFinishPairingOrAuthResponse>) => {
        if(jwtToken) {
          console.debug(`Response after submitting mfa code: ${jwtToken.mfaUserDetails.errorId}: ${jwtToken.mfaUserDetails.errorMsg}`);
          if(jwtToken.mfaUserDetails.errorId === PingErrorId.ok) {
            this.newJwtToken.emit(jwtToken);
          } else if(jwtToken.mfaUserDetails.errorId === PingErrorId.incorrectCode) {
            // this._showIncorrectCode = true;
            this.showIncorrectCode = true;
          }
        } else {
          // this._showIncorrectCode = true;
          this.showIncorrectCode = true;
        }
      });
    }
  }

  resendCode() {
    this.resetForm();
    this.authenticateWith().subscribe(() => this.notificationService.displayI18n('content.code-sent'));
  }

  chooseOtherDevice(returnForm: 'otp' | 'appFailure') {
    this.resetForm();
    this.jwtToken?.mfaUserDetails?.devicesDetails.forEach(d => console.debug(`Other device: ${JSON.stringify(d)}`));
    this.showOtherWays = true;
    if(returnForm === 'otp') {
      this.cancelOtherWaysCallback = () => this.showOtpForm = true;
    } else {
      this.cancelOtherWaysCallback = () => this.showAppFailure = true;
    }
  }

  /** Starts the authentication for the provided device.  Returns an observable that triggers when the code is first sent to the device. */
  authenticateWith(device: DeviceDetails = this.lastUsedDevice): Observable<void> {
    this.lastUsedDevice = device;

    const isApp = device.type === 'Android' || device.type === 'iPhone';
    const mfaUserDeviceId: MfaUserDeviceId = {userMFADetails: {userName: this.userName}, deviceId: device.deviceId};
    const mfaUserSessionId: MfaUserSessionId = {userMFADetails: {userName: this.userName}, sessionId: ''};

    // The Observable that will be used to notify that a code has been sent to the device.
    const codeSent$ = new Subject<void>();

    if(isApp) {
      this.mfaAuthenticationDisplayId = device.type;
      this.mfaService.startAuthOnline(mfaUserDeviceId).pipe(
        tap(() => {
          this.initialRequestSent = true;
          this.showAppDetails = true;
          codeSent$.next();
          codeSent$.complete();
        }),
        mergeMap(mfaAuthenticationResponse => {
          mfaUserSessionId.sessionId = mfaAuthenticationResponse.sessionId;
          //If we get back an error that says to enter the code through the email, ping decided to send the user an email instead of doing it with the app
          //This happens when the app is a secondary device
          if(mfaAuthenticationResponse.errorId === PingErrorId.enterCodeFromEmail || mfaAuthenticationResponse.errorId === PingErrorId.enterCodeFromApp) {
            this.mfaAuthenticationResponse = mfaAuthenticationResponse;
            this.mfaAuthenticationDisplayId = this.getMfaAuthenticationDisplayId(mfaAuthenticationResponse);
            this.showOtpForm = true;
            return of(true);
          } else {
            return this.mfaService.awaitOnlineAuth(mfaUserSessionId).pipe(tap(jwtToken => {
              if(jwtToken) {
                console.info('App authentication successful');
                this.newJwtToken.emit(jwtToken);
              } else {
                console.info('App authentication has failed');
                this.showAppFailure = true;
              }
            }));
          }
        })
      ).subscribe(); //The completion logic is within the different observables that can be returned in the mergeMap
    } else {
      this.mfaService.startAuthentication(mfaUserDeviceId).subscribe(mfaAuthenticationResponse => {
        this.initialRequestSent = true;
        this.showOtpForm = true;
        this.mfaAuthenticationResponse = mfaAuthenticationResponse;
        this.mfaAuthenticationDisplayId = this.getMfaAuthenticationDisplayId(mfaAuthenticationResponse);
        codeSent$.next();
        codeSent$.complete();
      });
    }

    return codeSent$;
  }

  logout() {
    this.cancel.emit();
  }

  get showAppDetails(): boolean {
    return this._showAppDetails;
  }

  set showAppDetails(value: boolean) {
    //Show that the user needs to accept the auth request on their ping app
    this._showAppDetails = value;
    if(value) {
      this._showIncorrectCode = false;
      this._showAppFailure = false;
      this._showOtpForm = false;
      this._showOtherWays = false;
    }
  }

  get showAppFailure(): boolean {
    return this._showAppFailure;
  }

  set showAppFailure(value: boolean) {
    //Show that authenticating with the app has failed
    this._showAppFailure = value;
    if(value) {
      this._showIncorrectCode = false;
      this._showAppDetails = false;
      this._showOtpForm = false;
      this._showOtherWays = false;
    }
  }

  get showOtpForm(): boolean {
    return this._showOtpForm;
  }

  set showOtpForm(value: boolean) {
    //Show the form that asks for the otp code
    //Possible to show incorrect code error
    this._showOtpForm = value;
    if(value) {
      this._showAppDetails = false;
      this._showAppFailure = false;
      this._showOtherWays = false;
    }
  }

  get showOtherWays(): boolean {
    return this._showOtherWays;
  }

  set showOtherWays(value: boolean) {
    //Show the possible devices to authenticate with
    this._showOtherWays = value;
    if(value) {
      this._showAppDetails = false;
      this._showAppFailure = false;
      this._showIncorrectCode = false;
      this._showOtpForm = false;
    } else {
      //When showing other ways, a callback will be set to undo it
      //The callback will be a simple call to one of these setter methods (either showOtpForm or showAppFailure)
      // note: it's only shown possible to choose a different way during app auth when it fails
      if(this.cancelOtherWaysCallback) {
        this.cancelOtherWaysCallback();
      } else {
        console.warn('Cancel Other-Ways callback not set');
      }
    }
  }

  get showIncorrectCode(): boolean {
    return this._showIncorrectCode;
  }

  set showIncorrectCode(value: boolean) {
    //Show that the otp code entered was wrong
    //Only shown on the otp form
    this._showIncorrectCode = value;
    if(value) {
      this.showOtpForm = true;
    }
  }
}
